/**
 * Cookie utilities for app download promo frequency control
 * Based on magua mosaic-provider-app-download-promos-service cookie logic
 */

import type { CookiesRecord } from "../../../../../shared/util/CookieProvider";

import { setCookiesForViewJob } from "../../../../../shared/native-modules/core/setCookies";
import { CookieKey } from "../../../../../shared/native-modules/types/RNCoreNativeModule";

function parseCookieRecordValueAsInt(cookies: CookiesRecord, key: CookieKey): number {
  return Number(cookies[key]) || 0;
}

/**
 * Suppresses G1T (goal one tap) when promo is displayed
 */
export function suppressG1T(): void {
  void setCookiesForViewJob([{ name: CookieKey.maguaSuppressG1T, value: "1" }]);
}

/**
 * Enables G1T when conditions are met
 */
export function enableG1T(cookies: CookiesRecord, country?: string): void {
  const PAGES_TO_UNSUPPRESS_G1T = [0, 2, 4, 7, 11, 16];

  const monthlyOrWeeklyCount = parseCookieRecordValueAsInt(
    cookies,
    CookieKey.maguaPromoShownInMonthOrWeekCount,
  );
  const sessionCount = parseCookieRecordValueAsInt(
    cookies,
    CookieKey.maguaPromoShownInSessionCount,
  );
  const pageVisitCount = parseCookieRecordValueAsInt(cookies, CookieKey.maguaPageVisitsCount);

  const maxPromosPerSession = country === "US" ? 3 : 2;
  const maxPromosPerMonthOrWeek = 10;

  if (
    (monthlyOrWeeklyCount >= maxPromosPerMonthOrWeek || sessionCount >= maxPromosPerSession) &&
    PAGES_TO_UNSUPPRESS_G1T.includes(pageVisitCount)
  ) {
    void setCookiesForViewJob([{ name: CookieKey.maguaSuppressG1T, value: "0" }]);
  }
}

export function incrementPageVisitCountCookie(cookies: CookiesRecord): void {
  const pageVisitCount = parseCookieRecordValueAsInt(cookies, CookieKey.maguaPageVisitsCount);
  void setCookiesForViewJob([
    { name: CookieKey.maguaPageVisitsCount, value: `${pageVisitCount + 1}` },
  ]);
}

export function incrementSessionCountCookie(cookies: CookiesRecord): void {
  const sessionCount = parseCookieRecordValueAsInt(
    cookies,
    CookieKey.maguaPromoShownInSessionCount,
  );
  void setCookiesForViewJob([
    { name: CookieKey.maguaPromoShownInSessionCount, value: `${sessionCount + 1}` },
  ]);
}

export function incrementMonthOrWeeklyCountCookie(cookies: CookiesRecord): void {
  const monthlyOrWeeklyCount = parseCookieRecordValueAsInt(
    cookies,
    CookieKey.maguaPromoShownInMonthOrWeekCount,
  );
  void setCookiesForViewJob([
    { name: CookieKey.maguaPromoShownInMonthOrWeekCount, value: `${monthlyOrWeeklyCount + 1}` },
  ]);
}

export function incrementClosedCountInSessionCookie(cookies: CookiesRecord): void {
  const closedCount = parseCookieRecordValueAsInt(
    cookies,
    CookieKey.maguaPromoClosedInSessionCount,
  );
  void setCookiesForViewJob([
    { name: CookieKey.maguaPromoClosedInSessionCount, value: `${closedCount + 1}` },
  ]);
}

export function incrementClosedCountInMonthCookie(cookies: CookiesRecord): void {
  const closedCount = parseCookieRecordValueAsInt(cookies, CookieKey.maguaPromoClosedInMonthCount);
  void setCookiesForViewJob([
    { name: CookieKey.maguaPromoClosedInMonthCount, value: `${closedCount + 1}` },
  ]);
}

/**
 * Determines if web to app promo should be shown based on cookies from CookieProvider.
 * Shows promo on page visits: 0, 2, 5, 9, 14
 */
export function shouldShowWebToAppPromo(cookies: CookiesRecord, country?: string): boolean {
  const PAGES_TO_SHOW_PROMO = [0, 2, 5, 9, 14];
  const maxPromosPerSession = country === "US" ? 3 : 2;
  const maxPromosPerMonthOrWeek = 10;

  const pageVisitCount = parseCookieRecordValueAsInt(cookies, CookieKey.maguaPageVisitsCount);
  const promoShownInSessionCount = parseCookieRecordValueAsInt(
    cookies,
    CookieKey.maguaPromoShownInSessionCount,
  );
  const promoShownInMonthOrWeekCount = parseCookieRecordValueAsInt(
    cookies,
    CookieKey.maguaPromoShownInMonthOrWeekCount,
  );

  // Don't show if monthly/weekly limit reached
  if (promoShownInMonthOrWeekCount >= maxPromosPerMonthOrWeek) return false;

  // Don't show if session limit reached
  if (promoShownInSessionCount >= maxPromosPerSession) return false;

  // Show only on specific page visits
  return PAGES_TO_SHOW_PROMO.includes(pageVisitCount);
}
